/**
 * # Time Range Slider
 * A dual slider for Time inputs.
 *
 * @example
 * new TimeRangeSlider(el, {
 *   // Optional. Slide along the x or y-axis?
 *   isX: true,
 *   onChange: function(inst, index, value){},
 * });
 *
 * @module components/time-range-slider.js
 */

import RangeSlider from './range-slider';
import TimeInput from './time-input';
import toggleClass from '../helpers/dom/toggle-class';
import getIndex from '../helpers/traversal/get-index';
import makeElement from '../helpers/dom/make-element';
import modulo from '../helpers/util/modulo';
import {string as parseStringAttribute} from '../helpers/dom/parse-attribute';
import {isValidTime, convertMinutesToTime, convertTimeToMinutes, getTimePeriod, convertTimeTo12Hr} from '../helpers/time/time.js';
import debounce from '../helpers/util/debounce';
import mixin from '../helpers/util/mixin';
import validation from '../mixins/validation';
import messaging from '../mixins/messaging';

const noop = function() {};

class TimeRangeSlider extends RangeSlider {

  /**
   * TimeRangeSlider constructor.
   * @param {Element} el
   * @param {Object} params
   */
  constructor(el, params = {}) {
    params.elRequired = true;

    if (!super(el, params)) {
      return;
    }

    this._initTypeaheads();
  }


  /**
   * Start the slider moving.
   * @param {Number} index The index of the handle or input element.
   * @param {Number} position The position of the pointer.
   * @param {String} type Optional Which type of events to listen for.
   */
  start(index, position, type) {

    // Noop if we're disabled or an invalid index was passed
    if (index < 0 || this.inputEls[index].querySelector('[type="time"]').getAttribute('disabled') !== null) {
      return this;
    }

    this._addMoveEventListeners(type || 'mouse');
    this._cacheSize();

    this.isActive = this.isActive || [];
    this.isActive[index] = true;

    this.currentIndex = index;
    this._updateActiveClasses(index);
    this._updateDisabledClasses();
    this._oldVal = this.values[index];
    this.move(position);

    return this;
  }


  /**
   * Store a reference to the slider and its associated elements.
   * @param {Element} el
   */
  _cacheElements(el) {
    this.el = el;
    this.controlsEl = this.el.querySelector('.spark-slider__controls');
    this.inputEls = this.el.querySelectorAll('.spark-time');
    this.handleEls = this.el.querySelectorAll('.spark-slider__handle');
    this.trackEl = this.el.querySelector('.spark-slider__track');
    this.trackFillEl = this.trackEl.querySelector('.spark-slider__track-fill');
    this.messageEl = this.el.querySelector('.spark-slider__message') || makeElement('<span class="spark-slider__message"></span>');

    if (!this.inputEls || this.inputEls.length <= 1) {
      throw new Error('Tried to create a slider instance without two text inputs.');
    }

    if (!this.handleEls || this.handleEls.length <= 1) {
      throw new Error('Tried to create a slider instance without two handle buttons.');
    }
  }


  /**
   * Parse parameters from the elements and set initial values and handle positions.
   */
  _parseParams() {
    let lowestMin = Infinity;
    let highestMax = -Infinity;

    const values = [];
    this.mins = [];
    this.maxes = [];
    this.steps = [];
    this.valuePrecision = [];
    this.typeaheads = [];
    this.timeFormat = this.timeFormat !== null ? this.timeFormat : parseStringAttribute(this.el, 'data-time-format', '12');
    this.customAriaLabels = this.customAriaLabels !== null ? this.customAriaLabels : null;

    // Cache the size of the element so that we can properly set values on handles.
    this._cacheSize();

    // Set the minimum and max values for each element. Also set any predefined value.
    for (let i = 0, numInputs = this.inputEls.length; i < numInputs; i++) {

      // Parse and set min
      let parsedMin = this.inputEls[i].querySelector('[type="time"]').getAttribute('min');

      if (parsedMin === null) {

        // Set to 12:00 a.m. in minutes
        this.mins[i] = 0;
      } else {
        if (!isValidTime(parsedMin) && this.timeFormat !== isValidTime(parsedMin)) {
          throw new Error('The provided min time is not valid. Please refer to the Time Selector documentation for accepted time notations.');
        }

        this.mins[i] = convertTimeToMinutes(parsedMin);
      }

      // Parse and set max
      let parsedMax = this.inputEls[i].querySelector('[type="time"]').getAttribute('max');

      if (parsedMax === null) {

        // Set to 11:59 p.m. in minutes
        this.maxes[i] = 1439;
      } else {
        if (!isValidTime(parsedMax) && this.timeFormat !== isValidTime(parsedMax)) {
          throw new Error('The provided max time is not valid. Please refer to the Time Selector documentation for accepted time notations.');
        }

        this.maxes[i] = convertTimeToMinutes(parsedMax);
      }

      // Step should be in minutes, hence an integer. Will defaut to 60 minutes if not provided
      this.steps[i] = parseInt(this.inputEls[i].querySelector('[type="time"]').getAttribute('step')) || 60;

      // Default precision for Integers
      this.valuePrecision[i] = 1;

      if (this.mins[i] < lowestMin) {
        lowestMin = this.mins[i];
      }

      if (this.maxes[i] > highestMax) {
        highestMax = this.maxes[i];
      }
    }

    this.lowestMin = lowestMin;
    this.highestMax = highestMax;

    // If we have a default value, set it. Otherwise resort to min and max
    for (let i = 0, numInputs = this.inputEls.length; i < numInputs; i++) {

      values[i] = this.inputEls[i].querySelector('[type="time"]').getAttribute('value');

      if (values[i] === null) {
        if (i + 1 === numInputs) {
          this.setValue(i, this.maxes[i] !== null ? this.maxes[i] : 0);
        } else {
          this.setValue(i, this.mins[i] !== null ? this.mins[i] : 0);
        }
      } else {
        this.setValue(i, values[i]);
      }
    }
  }


  /**
   * Enable the Slider handles and inputs.
   * @param {Number} index
   */
  enable(index) {

    // We don't have an input element at that index, so something went wrong.
    if (!this.typeaheads[index] || !this.handleEls[index]) {
      throw new Error('Cannot find a slider input element with an index of ' + index + '. That element does not exist.');
    }

    this.typeaheads[index].enable();
    this.handleEls[index].removeAttribute('disabled');
    this.handleEls[index].removeAttribute('tabindex');
    this._updateDisabledClasses();

    return this;
  }


  /**
   * Disable the Slider handles and inputs.
   * @param {Number} index
   */
  disable(index) {

    // We don't have an input element at that index, so something went wrong.
    if (!this.typeaheads[index] || !this.handleEls[index]) {
      throw new Error('Cannot find a slider input element with an index of ' + index + '. That element does not exist.');
    }

    this.typeaheads[index].disable();
    this.handleEls[index].setAttribute('disabled', '');
    this.handleEls[index].setAttribute('tabindex', '-1');
    this._updateDisabledClasses();

    return this;
  }


  /**
   * Update which handles are disabled.
   */
  _updateDisabledClasses() {
    let disabledCount = 0;

    for (let i = 0, len = this.inputEls.length; i < len; i++) {

      const timeInput = this.inputEls[i].querySelector('[type="time"]');

      if (timeInput.getAttribute('disabled') !== null) {
        toggleClass(this.handleEls[i], 'disabled', true);
        disabledCount++;
      } else {
        toggleClass(this.handleEls[i], 'disabled', false);
      }
    }

    toggleClass(this.el, 'all-disabled', disabledCount === this.handleEls.length);
  }


  /**
   * Create bound versions of event listener callbacks and store them.
   * Otherwise we can't unbind from these events later because the
   * function signatures won't match.
   */
  _bindEventListenerCallbacks() {
    this._onTouchStartBound = this._onTouchStart.bind(this);
    this._onTouchMoveBound = this._onTouchMove.bind(this);
    this._onTouchEndBound = this._onTouchEnd.bind(this);
    this._onClickBound = this._onClick.bind(this);
    this._onMouseDownBound = this._onMouseDown.bind(this);
    this._onMouseMoveBound = this._onMouseMove.bind(this);
    this._onMouseUpBound = this._onMouseUp.bind(this);
    this._onInputBlurBound = this._onInputBlur.bind(this);
    this._onInputChangeBound = this._onInputChange.bind(this);
    this._onFocusBound = this._onFocus.bind(this);
    this._onKeydownBound = this._onKeydown.bind(this);
    this._onBlurBound = this._onBlur.bind(this);
    this._onResizeBound = debounce(this._onResize.bind(this), 90);
    this._onVisibleChildrenBound = this._onVisibleChildren.bind(this);
    this._onWindowClickBound = this._onWindowClick.bind(this);
  }


  /**
   * Add event listeners for touchstart and mouse click.
   */
  _addEventListeners() {
    this.controlsEl.addEventListener('touchstart', this._onTouchStartBound);
    this.controlsEl.addEventListener('mousedown', this._onMouseDownBound);

    for (let i = 0, len = this.handleEls.length; i < len; i++) {
      this.handleEls[i].addEventListener('focus', this._onFocusBound);
      this.handleEls[i].addEventListener('click', this._onClickBound);
    }

    document.addEventListener('spark.visible-children', this._onVisibleChildrenBound, true);

    window.addEventListener('resize', this._onResizeBound);
    window.addEventListener('orientationchange', this._onResizeBound);
    window.addEventListener('click', this._onWindowClickBound);
  }


  /**
   * Remove event listeners for touchstart and mouse click.
   */
  _removeEventListeners() {
    this.controlsEl.removeEventListener('touchstart', this._onTouchStartBound);
    this.controlsEl.removeEventListener('mousedown', this._onMouseDownBound);

    document.removeEventListener('spark.visible-children', this._onVisibleChildrenBound);

    for (let j = 0, len2 = this.handleEls.length; j < len2; j++) {
      this.handleEls[j].removeEventListener('focus', this._onFocusBound);
      this.handleEls[j].removeEventListener('click', this._onClickBound);
    }

    window.removeEventListener('resize', this._onResizeBound);
    window.removeEventListener('orientationchange', this._onResizeBound);
    window.removeEventListener('click', this._onWindowClickBound);
  }


  /**
   * Initialize the typeahead inputs.
   */
  _initTypeaheads() {
    if (this.inputEls) {
      for (let i = 0; i < this.inputEls.length; i++) {

        let hourAriaLabel = null;
        let minuteAriaLabel = null;
        let periodAriaLabel = null;

        if (this.customAriaLabels) {
          hourAriaLabel = this.customAriaLabels[i].hourAriaLabel;
          minuteAriaLabel = this.customAriaLabels[i].minuteAriaLabel;
          periodAriaLabel = this.customAriaLabels[i].periodAriaLabel;

          if (hourAriaLabel === '' || hourAriaLabel === undefined) {
            hourAriaLabel = null;
          }

          if (minuteAriaLabel === '' || minuteAriaLabel === undefined) {
            minuteAriaLabel = null;
          }

          if (periodAriaLabel === '' || periodAriaLabel === undefined) {
            periodAriaLabel = null;
          }
        }

        this.typeaheads[i] = new TimeInput(this.inputEls[i], {
          timeFormat: this.timeFormat,
          hourAriaLabel: hourAriaLabel,
          minuteAriaLabel: minuteAriaLabel,
          periodAriaLabel: periodAriaLabel,
          onBlur: this._onInputBlurBound,
          onChange: this._onInputChangeBound
        });
      }
    }
  }


  /**
   * Get the value as a valid time in desired format.
   * @param {Number} index
   */
  getValue(index) {
    if (!this.inputEls[index]) {
      throw new Error('Cannot get value from a slider input element with an index of ' + index + '. That element does not exist.');
    }

    return convertMinutesToTime(this.values[index], this.timeFormat);
  }


  /**
   * Set the value of the handle.
   * @param {Number} index The index of the input element.
   * @param {Number} value
   */
  setValue(index, value) {

    // We don't have an input element at that index, so something went wrong.
    if (!this.inputEls[index]) {
      throw new Error('Cannot set value on a slider input element with an index of ' + index + '. That element does not exist.');
    }

    if (isNaN(value)) {
      value = (typeof value === 'string' && value !== '') ? convertTimeToMinutes(value) : undefined;
    }

    // If the value is invalid due to invalid user input, set to the min to recover gracefully and not blow up the slider
    if (value === undefined) value = this.mins[index];

    // Move in increments if we have a defined step size
    if (this.steps[index]) {
      value = value - modulo(value, this.steps[index]);
    }

    value = Math.round(value * this.valuePrecision[index]) / this.valuePrecision[index];

    this.values = this.values || [];

    // Check bounds of the new value
    if (value > this.maxes[index]) {
      value = this.maxes[index];
    } else if (value < this.mins[index]) {
      value = this.mins[index];
    }

    if (this.values[index + 1] !== undefined && value >= this.values[index + 1]) {
      value = this.values[index + 1] - (this.steps[index] || 1);
    } else if (this.values[index - 1] !== undefined && value <= this.values[index - 1]) {
      value = this.values[index - 1] + (this.steps[index] || 1);
    }

    // Store value
    this.values[index] = value;

    // Update elements
    const timeValue = [];
    timeValue['12'] = convertMinutesToTime(this.values[index], '12');
    timeValue['24'] = convertMinutesToTime(this.values[index]);

    this.inputEls[index].querySelector('[type="time"]').value = timeValue['24'];
    this.handleEls[index].setAttribute('data-value', timeValue[this.timeFormat]);
    this.handleEls[index].setAttribute('data-value-minutes', this.values[index]);
    this.handleEls[index].setAttribute('aria-valuenow', timeValue[this.timeFormat]);
    this.handleEls[index].setAttribute('aria-valuetext', timeValue[this.timeFormat]);

    // Update Typeaheads
    if (this.typeaheads[index] !== undefined) {
      let [hours, minutes] = timeValue[this.timeFormat].split(':');
      let period;

      if (minutes.includes('a.m.') || minutes.includes('p.m.')) {
        period = minutes.substr(minutes.length - 4);
        minutes = minutes.substr(0, minutes.length - 4);
      }

      let values = {
        hour: hours,
        minute: minutes
      };

      if (this.timeFormat === '12') values.period = period;

      this.typeaheads[index].setValue(values);
    }

    // Set the percentage
    this.percentages = this.percentages || [];
    this.percentages[index] = (this.values[index] - this.lowestMin) / (this.highestMax - this.lowestMin);

    // Update the position of the handle
    this._updateHandlePosition(index);

    return this;
  }


  /**
   * When a key is pressed, see if it's one of the Arrow, Page up, Page down, Home
   * or End keys move the handle accordingly.
   * @param {Object} e
   */
  _onKeydown(e) {
    if (getIndex(this.inputEls, e.target) !== -1) {
      return;
    }

    if (e.keyCode === 39 || e.keyCode === 38 || e.keyCode === 33) {
      // Right or Up arrow or Page Up
      this.increment(false);
    } else if (e.keyCode === 37 || e.keyCode === 40 || e.keyCode === 34) {
      // Left or down arrow
      this.decrement(false);
    } else if (e.keyCode === 35) {
      // End
      this.setValue(this.max);
    } else if (e.keyCode === 36) {
      // Home
      this.setValue(this.min);
    }
  }


  /**
   * When an input is blurred, update the slider values
   * @param {Number} val
   * @param {Object} inst An instance of a Time Input
   */
  _onInputBlur(val, inst) {
    const index = getIndex(this.inputEls, inst.el);

    this._updateDisabledClasses();

    if (convertTimeToMinutes(inst.inputEl.value) !== this.values[index]) {
      this.setValue(index, inst.inputEl.value);
    }

    (this.onChange || noop)(index, convertMinutesToTime(this.values[index], this.timeFormat), this);
  }


  /**
   * When an input value is changed, update the slider values
   * @param {Number} val
   * @param {Object} inst An instance of a Time Input
   */
  _onInputChange(val, inst) {
    const index = getIndex(this.inputEls, inst.el);

    this._updateDisabledClasses();

    if (inst.inputEl.value === '') {
      const minute = inst.typeaheads.minute.getValue(true);

      // If the minute field is emptied by the user, set it to 00
      if (minute === '' || minute === null) {
        inst.typeaheads.minute.setValue('00');
        inst.updateInput();
      }

      // If the period field is emptied by the user, set it to what it was before
      if (this.timeFormat === '12') {
        const period = inst.typeaheads.period.getValue();

        if (period === '' || period === null) {
          const oldValue = convertTimeTo12Hr(inst.currValue);
          inst.typeaheads.period.setValue(getTimePeriod(oldValue));
          inst.updateInput();
        }
      }
    }

    if (convertTimeToMinutes(inst.inputEl.value) !== this.values[index]) {
      this.setValue(index, inst.inputEl.value);
    }

    (this.onChange || noop)(index, convertMinutesToTime(this.values[index], this.timeFormat), this);
  }


  /**
   * Update the element and handle positions.
   * @param {Element} el
   */
  update(el) {
    this._removeEventListeners();
    this._removeMoveEventListeners('touch');
    this._removeMoveEventListeners('mouse');
    this._removeMoveEventListeners('keyboard');

    this._cacheElements(el);
    this._parseParams();
    this._addEventListeners();
    this._updateDisabledClasses();

    for (let i = 0; i < this.handleEls.length; i++) {
      this._updateHandlePosition(i);
    }

    return this;
  }
}


/**
 * Whitelisted parameters which can be set on construction.
 * @type {Array}
 */
TimeRangeSlider.prototype._whitelistedParams = [
  'validate',
  'onValidate',
  'onChange',
  'timeFormat',
  'customAriaLabels'
];


/**
 * Default values for internal properties we will be setting.
 * These are set on each construction so we don't leak properties
 * into the prototype chain.
 * @type {Object}
 */
TimeRangeSlider.prototype.defaults = {
  el: null,
  messageEl: null,
  controlsEl: null,
  inputEls: null,
  handleEls: null,
  trackEl: null,
  trackFillEl: null,
  isActive: null,
  isX: true,
  onValidate: null,
  onChange: null,
  position: 0,
  width: 0,
  height: 0,
  mins: null,
  maxes: null,
  steps: null,
  interval: null,
  values: null,
  percentages: null,
  offsetLeft: 0,
  offsetTop: 0,
  handleSizePercentage: 0,
  currentIndex: null,
  lastIndex: null,
  _oldVal: null,
  timeFormat: null,
  customAriaLabels: null,
  _onTouchStartBound: null,
  _onTouchMoveBound: null,
  _onTouchEndBound: null,
  _onMouseDownBound: null,
  _onMouseMoveBound: null,
  _onMouseUpBound: null,
  _onMouseOutBound: null,
  _onFocusBound: null,
  _onKeydownBound: null,
  _onBlurBound: null,
  _onResizeBound: null,
  _onClickBound: null,
  _onVisibleChildrenBound: null
};

mixin(TimeRangeSlider.prototype, messaging, validation);

export default TimeRangeSlider;
